'use client'

import { useEffect, useState } from 'react'

import { type PortableTextBlock } from 'next-sanity'
import { useIsPresentationTool } from 'next-sanity/hooks'

import { Icon } from '@/app/components/Icon'
import CustomPortableText from '@/app/components/PortableText'
import PortableTextMinimal from '@/app/components/PortableTextMinimal'
import { TRACKING_LOCATIONS, TrackingLocation } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'

type AccordionProps = {
  value: {
    heading: PortableTextBlock[]
    content: PortableTextBlock[]
  }
  tracking: TrackingLocation
  documentId?: string
  documentType?: string
  parentPath?: string
}

export function Accordion({
  value,
  tracking,
  documentId,
  documentType,
  parentPath,
}: AccordionProps) {
  const isPresentationTool = useIsPresentationTool()
  const [isOpen, setIsOpen] = useState(false)

  // Keep accordion open in Sanity presentation mode
  useEffect(() => {
    if (isPresentationTool) {
      setIsOpen(true)
    }
  }, [isPresentationTool])

  if (!value.heading || !value.content) {
    return null
  }

  return (
    <div className="overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between pt-sm pb-xl transition-colors text-left cursor-pointer"
        aria-expanded={isOpen}
      >
        <p
          className={cn(
            'flex-1 size-header-h5 pr-lg',
            isOpen ? 'text-white-950' : 'text-white-800',
          )}
        >
          <PortableTextMinimal
            value={value.heading as PortableTextBlock[]}
            tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
          />
        </p>
        <div
          className={cn(
            'transition-transform duration-200 flex-shrink-0 w-[46px] h-[46px] bg-neutral-50 rounded-xs flex items-center justify-center',
            isOpen && 'rotate-180',
          )}
        >
          <Icon
            name={isOpen ? 'faMinus' : 'faPlus'}
            provider="fontawesome"
            style="regular"
            size={22}
            className="text-neutral-950"
          />
        </div>
      </button>

      <div
        className={cn(
          'overflow-hidden transition-all duration-300 linear',
          isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div className="flex flex-col mb-5xl">
          <CustomPortableText
            value={value.content}
            tracking={tracking}
            documentId={documentId}
            documentType={documentType}
            parentPath={parentPath}
            paragraphClassName="text-white-700"
          />
        </div>
      </div>

      <div className="h-[1px] bg-neutral-100" />
    </div>
  )
}
